<script lang="ts" module>
  // A trigger plus the overflow menu it opens (wiki/feature-design-system.md).
  // Escape closes and returns focus, a pointer outside closes, ↑/↓/Home/End walk.

  export interface MenuAction {
    id: string;
    label: string;
    onselect: () => void;
    disabled?: boolean;
    /** Why it is disabled — rendered as `title` + the accessible name. */
    title?: string;
    tone?: 'default' | 'danger';
    separated?: boolean;
  }

  // Only one menu on the page is open, even when reached by keyboard. A symbol,
  // never an object: a `$state` proxy is not `===` the value it proxies.
  let openToken = $state<symbol | null>(null);
</script>

<script lang="ts">
  import { onDestroy, tick } from 'svelte';
  import { EllipsisVertical, type LucideIcon } from '$lib/ui/icons';

  interface Props {
    label: string;
    menuLabel?: string;
    items: MenuAction[];
    icon?: LucideIcon;
    text?: string;
    align?: 'start' | 'end';
    'data-testid'?: string;
    onOpenChange?: (open: boolean) => void;
  }

  let {
    label,
    menuLabel,
    items,
    icon: Icon = EllipsisVertical,
    text,
    align = 'end',
    'data-testid': dataTestid,
    onOpenChange,
  }: Props = $props();

  const token = Symbol('menu');
  let rootEl = $state<HTMLDivElement | undefined>(undefined);
  let triggerEl = $state<HTMLButtonElement | undefined>(undefined);
  const open = $derived(openToken === token);
  let up = $state(false);

  $effect(() => {
    onOpenChange?.(open);
  });

  function entries(): HTMLButtonElement[] {
    return [...(rootEl?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
  }

  async function toggle(): Promise<void> {
    if (open) {
      close();
      return;
    }
    up = false;
    openToken = token;
    await tick();
    up = overflowsBelow();
    entries().find((el) => !el.disabled)?.focus();
  }

  // Opens upward when it would spill past the nearest clipping box.
  function overflowsBelow(): boolean {
    const panel = rootEl?.querySelector('[role="menu"]');
    if (!panel || !rootEl) return false;
    let floor = window.innerHeight;
    for (let el = rootEl.parentElement; el; el = el.parentElement) {
      if (getComputedStyle(el).overflowY !== 'visible') floor = Math.min(floor, el.getBoundingClientRect().bottom);
    }
    return panel.getBoundingClientRect().bottom > floor;
  }

  function close(returnFocus = true): void {
    if (!open) return;
    openToken = null;
    if (returnFocus) triggerEl?.focus();
  }

  function select(item: MenuAction): void {
    close();
    item.onselect();
  }

  function moveFocus(step: number, from?: 'start' | 'end'): void {
    const all = entries().filter((el) => !el.disabled);
    if (all.length === 0) return;
    if (from) {
      (from === 'start' ? all[0] : all[all.length - 1]).focus();
      return;
    }
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    all[(at + step + all.length) % all.length].focus();
  }

  function onMenuKey(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown') moveFocus(1);
    else if (e.key === 'ArrowUp') moveFocus(-1);
    else if (e.key === 'Home') moveFocus(0, 'start');
    else if (e.key === 'End') moveFocus(0, 'end');
    else return;
    e.preventDefault();
  }

  $effect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    }
    function onPointer(e: Event): void {
      if (rootEl && !rootEl.contains(e.target as Node)) close(false);
    }
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onPointer, true);
    };
  });

  // An unmount while open must release the page-wide token.
  onDestroy(() => {
    if (openToken === token) openToken = null;
  });
</script>

<div class="vd-menu" bind:this={rootEl}>
  <button
    type="button"
    class="vd-menu__trigger"
    class:vd-menu__trigger--text={text}
    bind:this={triggerEl}
    onclick={toggle}
    aria-label={label}
    title={label}
    aria-haspopup="menu"
    aria-expanded={open}
    data-testid={dataTestid}
  >
    <Icon size={text ? 16 : 18} strokeWidth={1.75} aria-hidden="true" focusable="false" />{text}
  </button>
  {#if open}
    <div
      class="vd-menu__panel"
      class:vd-menu__panel--start={align === 'start'}
      class:vd-menu__panel--up={up}
      role="menu"
      tabindex="-1"
      aria-label={menuLabel ?? label}
      onkeydown={onMenuKey}
    >
      {#each items as item (item.id)}
        <button
          type="button"
          role="menuitem"
          class="vd-menu__item"
          class:vd-menu__item--danger={item.tone === 'danger'}
          class:vd-menu__item--separated={item.separated}
          disabled={item.disabled}
          title={item.title}
          aria-label={item.title ? `${item.label} — ${item.title}` : undefined}
          data-testid={item.id}
          onclick={() => select(item)}
        >{item.label}</button>
      {/each}
    </div>
  {/if}
</div>

<style lang="scss">
  .vd-menu {
    position: relative;
    display: inline-flex;
    flex: none;
  }

  /* 44px at every pointer type: often the card's only action target. */
  .vd-menu__trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 1px solid var(--line);
    border-radius: 9px;
    background: transparent;
    color: var(--ink-soft);
    cursor: pointer;
    line-height: 1;
    transition:
      border-color 0.2s ease,
      background 0.2s ease,
      color 0.2s ease;
  }

  .vd-menu__trigger--text {
    gap: 0.35rem;
    width: auto;
    padding: 0 0.9rem;
    border-style: dashed;
    border-radius: 999px;
    font: inherit;
    font-size: var(--text-meta);
    white-space: nowrap;
  }

  .vd-menu__trigger:hover {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 6%, transparent);
    color: var(--ink);
  }

  .vd-menu__trigger:focus-visible {
    outline: 2px solid var(--accent-text);
    outline-offset: 2px;
  }

  /* Solid paper: the danger red has no contrast headroom for a tint. */
  .vd-menu__panel {
    position: absolute;
    top: calc(100% + 0.35rem);
    right: 0;
    z-index: 50;
    display: flex;
    flex-direction: column;
    min-width: 13rem;
    max-width: min(18rem, calc(100vw - 2rem));
    padding: 0.35rem;
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.18);
  }

  .vd-menu__panel--up {
    top: auto;
    bottom: calc(100% + 0.35rem);
  }

  .vd-menu__panel--start {
    right: auto;
    left: 0;
  }

  .vd-menu__item {
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    min-height: 44px;
    padding: 0.5rem 0.7rem;
    border: none;
    border-radius: 6px;
    background: none;
    color: var(--ink);
    font: inherit;
    font-size: var(--text-body);
    text-align: left;
    cursor: pointer;
  }

  .vd-menu__item:hover:not(:disabled),
  .vd-menu__item:focus-visible {
    background: color-mix(in srgb, var(--accent) 8%, transparent);
  }

  .vd-menu__item:disabled {
    color: var(--ink-mute);
    cursor: default;
  }

  .vd-menu__item--danger {
    color: var(--color-recording-text);
  }

  /* A ring, never a wash: the red text is exactly 4.50 on dark paper. */
  .vd-menu__item--danger:hover:not(:disabled),
  .vd-menu__item--danger:focus-visible {
    background: none;
    outline: 2px solid var(--color-recording);
    outline-offset: -2px;
  }

  .vd-menu__item--separated {
    margin-top: 0.3rem;
    border-top: 1px solid var(--line);
    border-radius: 0 0 6px 6px;
  }
</style>
